import { ExternalLink, ImageOff } from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { cn } from '@/shared/lib/utils';

export interface ListingCardProps {
  title?: string;
  url?: string;
  photos?: string[];
  priceThb?: string;
  usdPrice?: string;
  priceQuote?: string;
  quoteCurrency?: string;
  district?: string;
  areaSqm?: string;
  propertyType?: string;
  className?: string;
}

function formatMoney(raw: string | undefined, currency: string): string | null {
  if (!raw) return null;
  const trimmed = raw.replace(/,/g, '').trim();
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) {
    return `${currency} ${raw.trim()}`;
  }
  const digits = value >= 1000 ? 0 : 2;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: digits,
    }).format(value);
  } catch {
    return `${currency} ${new Intl.NumberFormat('en-US', { maximumFractionDigits: digits }).format(value)}`;
  }
}

export function ListingCard({
  title,
  url,
  photos,
  priceThb,
  usdPrice,
  priceQuote,
  quoteCurrency,
  district,
  areaSqm,
  propertyType,
  className,
}: ListingCardProps) {
  const { t } = useI18n();
  const label = title?.replace(/\s+/g, ' ').trim() || t.common.listingFallback;
  const cover = photos?.find(Boolean);
  const thbLabel = formatMoney(priceThb, 'THB');
  const quoteLabel =
    priceQuote && quoteCurrency ? formatMoney(priceQuote, quoteCurrency.toUpperCase()) : null;
  const usdLabel = formatMoney(usdPrice, 'USD');
  const priceLabel = thbLabel ?? usdLabel;
  const convertedLabel = thbLabel ? (quoteLabel ?? usdLabel) : null;
  const meta = [propertyType, district, areaSqm ? `${areaSqm} m²` : null]
    .filter(Boolean)
    .join(' · ');
  const href = url?.trim() || undefined;

  const body = (
    <>
      <div className="bg-muted relative aspect-[16/10] w-full overflow-hidden">
        {cover ? (
          <img
            src={cover}
            alt=""
            loading="lazy"
            className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="text-muted-foreground flex size-full items-center justify-center">
            <ImageOff className="size-5" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1 p-3">
        <div className="line-clamp-2 text-sm leading-snug font-medium">{label}</div>
        {priceLabel ? (
          <div className="text-sm font-semibold tracking-tight">{priceLabel}</div>
        ) : null}
        {convertedLabel ? (
          <div className="text-muted-foreground text-xs tracking-tight">{convertedLabel}</div>
        ) : null}
        {meta ? <div className="text-muted-foreground line-clamp-1 text-xs">{meta}</div> : null}
        {href ? (
          <div className="text-muted-foreground mt-1 inline-flex items-center gap-1 text-xs group-hover:text-foreground">
            {t.common.openListing}
            <ExternalLink className="size-3" />
          </div>
        ) : null}
      </div>
    </>
  );

  const shellClass = cn(
    'group bg-card text-card-foreground block min-w-0 max-w-[300px] overflow-hidden rounded-xl border border-border/80 shadow-sm',
    className,
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={label}
        className={cn(
          shellClass,
          'ring-offset-background transition-shadow hover:shadow-md focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        )}
      >
        {body}
      </a>
    );
  }

  return <div className={shellClass}>{body}</div>;
}
